/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Boxes, Plus, Wrench, Package, User, CheckCircle, FileText, Upload, Trash2, Image } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { prodottiAPI } from '../api/prodotti';
import { filesAPI } from '../api/files';

const ACCENT = '#8B5CF6';
const COLORI = { verde: '#10B981', arancione: '#F59E0B', rosso: '#EF4444', scaduta: '#DC2626' };
const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || '';

export default function Prodotti() {
  const [tab, setTab] = useState('installati');
  return (
    <AdminLayout title="Prodotti">
      <div style={styles.tabs}>
        <button onClick={() => setTab('installati')} style={{ ...styles.tab, ...(tab === 'installati' ? styles.tabActive : {}) }}>Prodotti installati</button>
        <button onClick={() => setTab('catalogo')} style={{ ...styles.tab, ...(tab === 'catalogo' ? styles.tabActive : {}) }}>Catalogo tipi</button>
      </div>
      {tab === 'installati' ? <Installati /> : <Catalogo />}
    </AdminLayout>
  );
}

// ============ TAB INSTALLATI ============
function Installati() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [clienteSel, setClienteSel] = useState(searchParams.get('cliente') || '');
  const [installaModal, setInstallaModal] = useState(false);
  const [registraProdotto, setRegistraProdotto] = useState(null);
  const [documentiProdotto, setDocumentiProdotto] = useState(null);

  const { data: clienti = [] } = useQuery({ queryKey: ['prod-clienti'], queryFn: () => prodottiAPI.clienti() });
  const { data: prodotti = [], isLoading } = useQuery({
    queryKey: ['prod-installati', clienteSel],
    queryFn: () => clienteSel ? prodottiAPI.prodottiCliente(clienteSel) : Promise.resolve([]),
    enabled: !!clienteSel,
  });

  return (
    <div>
      <div style={styles.toolbar}>
        <select value={clienteSel} onChange={(e) => setClienteSel(e.target.value)} style={styles.select}>
          <option value="">— Seleziona cliente —</option>
          {clienti.map((c) => <option key={c.id} value={c.id}>{c.nome} {c.cognome} ({c.email})</option>)}
        </select>
        <button onClick={() => setInstallaModal(true)} disabled={!clienteSel} style={{ ...styles.addBtn, opacity: clienteSel ? 1 : 0.5 }}>
          <Plus size={16} /> Installa prodotto
        </button>
      </div>

      {!clienteSel ? (
        <div style={styles.empty}><User size={36} color="#9CA3AF" /><p>Seleziona un cliente per vedere i suoi prodotti.</p></div>
      ) : isLoading ? (
        <div style={styles.empty}>Caricamento...</div>
      ) : prodotti.length === 0 ? (
        <div style={styles.empty}><Package size={36} color="#9CA3AF" /><p>Nessun prodotto installato per questo cliente.</p></div>
      ) : (
        <div style={styles.grid}>
          {prodotti.map((p) => (
            <div key={p.id} style={styles.prodCard}>
              <div style={styles.prodHead}>
                <div>
                  <div style={styles.prodTipo}>{p.tipo_nome}</div>
                  <div style={styles.prodMarca}>{[p.marca, p.modello].filter(Boolean).join(' ')}</div>
                  {p.ubicazione && <div style={styles.prodMeta}>{p.ubicazione}</div>}
                </div>
                <span style={{ ...styles.statoDot, backgroundColor: COLORI[p.stato_peggiore] }} />
              </div>
              <div style={styles.maintMini}>
                {p.manutenzioni.map((m) => (
                  <div key={m.manutenzione_tipo_id} style={styles.maintMiniRow}>
                    <span style={styles.maintMiniNome}>{m.nome}</span>
                    <div style={styles.miniTrack}><div style={{ ...styles.miniFill, width: `${m.percentuale_rimanente}%`, backgroundColor: COLORI[m.colore] }} /></div>
                  </div>
                ))}
              </div>
              <div style={styles.cardActions}>
                <button onClick={() => setRegistraProdotto(p)} style={{ ...styles.registraBtn, marginTop: 0 }}><CheckCircle size={15} /> Registra manutenzione</button>
                <button onClick={() => setDocumentiProdotto(p)} style={styles.documentiBtn}><FileText size={15} /> Documenti</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {installaModal && <InstallaModal clienteId={clienteSel} onClose={() => setInstallaModal(false)} onDone={() => { setInstallaModal(false); qc.invalidateQueries({ queryKey: ['prod-installati', clienteSel] }); }} />}
      {registraProdotto && <RegistraModal prodotto={registraProdotto} onClose={() => setRegistraProdotto(null)} onDone={() => { setRegistraProdotto(null); qc.invalidateQueries({ queryKey: ['prod-installati', clienteSel] }); }} />}
      {documentiProdotto && <DocumentiModal prodotto={documentiProdotto} onClose={() => setDocumentiProdotto(null)} />}
    </div>
  );
}

function InstallaModal({ clienteId, onClose, onDone }) {
  const { data: tipi = [] } = useQuery({ queryKey: ['prod-tipi'], queryFn: () => prodottiAPI.listaTipi() });
  const [form, setForm] = useState({ prodotto_tipo_id: '', marca: '', modello: '', matricola: '', ubicazione: '', anno_produzione: '', data_installazione: new Date().toISOString().slice(0,10), descrizione: '' });
  const [err, setErr] = useState('');

  const mut = useMutation({
    mutationFn: () => prodottiAPI.installa({ ...form, cliente_id: clienteId, anno_produzione: form.anno_produzione ? parseInt(form.anno_produzione) : null }),
    onSuccess: onDone,
    onError: (e) => setErr(e.response?.data?.error || 'Errore'),
  });

  const h = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  return (
    <div style={styles.overlay2} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Installa prodotto</h3>
        <div style={styles.field}>
          <label style={styles.lbl}>Tipo prodotto *</label>
          <select value={form.prodotto_tipo_id} onChange={h('prodotto_tipo_id')} style={styles.input}>
            <option value="">— Seleziona —</option>
            {tipi.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
        </div>
        <div style={styles.row}>
          <div style={styles.field}><label style={styles.lbl}>Marca</label><input value={form.marca} onChange={h('marca')} style={styles.input} /></div>
          <div style={styles.field}><label style={styles.lbl}>Modello</label><input value={form.modello} onChange={h('modello')} style={styles.input} /></div>
        </div>
        <div style={styles.row}>
          <div style={styles.field}><label style={styles.lbl}>Matricola</label><input value={form.matricola} onChange={h('matricola')} style={styles.input} /></div>
          <div style={styles.field}><label style={styles.lbl}>Anno produzione</label><input type="number" value={form.anno_produzione} onChange={h('anno_produzione')} style={styles.input} /></div>
        </div>
        <div style={styles.row}>
          <div style={styles.field}><label style={styles.lbl}>Ubicazione</label><input value={form.ubicazione} onChange={h('ubicazione')} style={styles.input} placeholder="es. Cucina" /></div>
          <div style={styles.field}><label style={styles.lbl}>Data installazione</label><input type="date" value={form.data_installazione} onChange={h('data_installazione')} style={styles.input} /></div>
        </div>
        <div style={styles.field}><label style={styles.lbl}>Descrizione</label><textarea value={form.descrizione} onChange={h('descrizione')} style={{ ...styles.input, minHeight: '50px', fontFamily: 'inherit' }} /></div>
        <div style={styles.infoBox}>Le manutenzioni del tipo scelto verranno collegate automaticamente con le cadenze di default.</div>
        {err && <div style={styles.errBox}>{err}</div>}
        <div style={styles.actions}>
          <button onClick={onClose} style={styles.cancelBtn}>Annulla</button>
          <button onClick={() => { setErr(''); if (!form.prodotto_tipo_id) { setErr('Seleziona il tipo'); return; } mut.mutate(); }} disabled={mut.isPending} style={styles.confirmBtn}>{mut.isPending ? 'Installo...' : 'Installa'}</button>
        </div>
      </div>
    </div>
  );
}

function RegistraModal({ prodotto, onClose, onDone }) {
  const [manutenzioneId, setManutenzioneId] = useState('');
  const [data, setData] = useState(new Date().toISOString().slice(0,10));
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');

  const mut = useMutation({
    mutationFn: () => prodottiAPI.registraManutenzione(prodotto.id, { manutenzione_tipo_id: manutenzioneId, data_effettuata: data, note: note || null }),
    onSuccess: onDone,
    onError: (e) => setErr(e.response?.data?.error || 'Errore'),
  });

  return (
    <div style={styles.overlay2} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Registra manutenzione</h3>
        <div style={styles.prodInfo}>{prodotto.tipo_nome} · {[prodotto.marca, prodotto.modello].filter(Boolean).join(' ')}</div>
        <div style={styles.field}>
          <label style={styles.lbl}>Manutenzione effettuata *</label>
          <select value={manutenzioneId} onChange={(e) => setManutenzioneId(e.target.value)} style={styles.input}>
            <option value="">— Seleziona —</option>
            {prodotto.manutenzioni.map((m) => <option key={m.manutenzione_tipo_id} value={m.manutenzione_tipo_id}>{m.nome}</option>)}
          </select>
        </div>
        <div style={styles.field}><label style={styles.lbl}>Data *</label><input type="date" value={data} onChange={(e) => setData(e.target.value)} style={styles.input} /></div>
        <div style={styles.field}><label style={styles.lbl}>Note</label><textarea value={note} onChange={(e) => setNote(e.target.value)} style={{ ...styles.input, minHeight: '50px', fontFamily: 'inherit' }} /></div>
        {err && <div style={styles.errBox}>{err}</div>}
        <div style={styles.actions}>
          <button onClick={onClose} style={styles.cancelBtn}>Annulla</button>
          <button onClick={() => { setErr(''); if (!manutenzioneId) { setErr('Seleziona la manutenzione'); return; } mut.mutate(); }} disabled={mut.isPending} style={styles.confirmBtn}>{mut.isPending ? 'Salvo...' : 'Registra'}</button>
        </div>
      </div>
    </div>
  );
}

// Documenti dell'impianto. Stanno attaccati a QUESTO apparecchio di QUESTO cliente,
// non al tipo di prodotto: due clienti con la stessa caldaia hanno libretto,
// garanzia e rapporti di controllo diversi. Sono le carte che il cliente cerca
// anni dopo — vendita, affitto, controlli — e le trova da solo nella sua area.
function DocumentiModal({ prodotto, onClose }) {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [categoria, setCategoria] = useState('libretto');
  const [err, setErr] = useState('');
  const [progresso, setProgresso] = useState(null);
  const [daEliminare, setDaEliminare] = useState(null);

  const { data: categorie = [] } = useQuery({ queryKey: ['categorie-documento'], queryFn: () => filesAPI.categorieDocumento() });
  const { data: documenti = [], isLoading } = useQuery({ queryKey: ['doc-prodotto', prodotto.id], queryFn: () => filesAPI.listaProdotto(prodotto.id) });
  const { data: stats } = useQuery({ queryKey: ['doc-prodotto-stats', prodotto.id], queryFn: () => filesAPI.statsProdotto(prodotto.id) });

  const ricarica = () => {
    qc.invalidateQueries({ queryKey: ['doc-prodotto', prodotto.id] });
    qc.invalidateQueries({ queryKey: ['doc-prodotto-stats', prodotto.id] });
  };

  const upload = useMutation({
    mutationFn: (file) => filesAPI.uploadProdotto(prodotto.id, file, categoria, setProgresso),
    onSuccess: () => { setProgresso(null); ricarica(); },
    onError: (e) => { setProgresso(null); setErr(e.response?.data?.error || 'Errore nel caricamento'); },
    onSettled: () => { if (inputRef.current) inputRef.current.value = ''; },
  });

  const elimina = useMutation({
    mutationFn: (id) => filesAPI.remove(id),
    onSuccess: () => { setDaEliminare(null); ricarica(); },
    onError: (e) => { setDaEliminare(null); setErr(e.response?.data?.error || 'Errore'); },
  });

  const etichetta = (chiave) => categorie.find((c) => c.chiave === chiave)?.etichetta || 'Altro';
  // Un libretto scansionato pesa qualche centinaio di KB: in MB si legge "0.00MB".
  const peso = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(b / 1024))}KB`);

  return (
    <div style={styles.overlay2} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Documenti dell'impianto</h3>
        <div style={styles.prodInfo}>
          {prodotto.tipo_nome} · {[prodotto.marca, prodotto.modello].filter(Boolean).join(' ')}
          {prodotto.ubicazione ? ` · ${prodotto.ubicazione}` : ''}
        </div>

        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.lbl}>Che documento è</label>
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)} style={styles.input}>
              {categorie.map((c) => <option key={c.chiave} value={c.chiave}>{c.etichetta}</option>)}
            </select>
          </div>
          <div style={{ ...styles.field, justifyContent: 'flex-end' }}>
            <label style={styles.lbl}>&nbsp;</label>
            <button onClick={() => inputRef.current && inputRef.current.click()} disabled={upload.isPending} style={{ ...styles.addBtn, opacity: upload.isPending ? 0.6 : 1 }}>
              <Upload size={16} /> {upload.isPending ? `Carico... ${progresso ?? 0}%` : 'Carica documento'}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,image/*"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) { setErr(''); upload.mutate(f); } }}
            />
          </div>
        </div>

        <div style={styles.infoBox}>
          PDF o immagini, fino a 50MB in tutto per apparecchio
          {stats ? ` (usati ${peso(stats.usato_bytes)}).` : '.'} Il cliente li vede nella sua area, sotto l'apparecchio.
        </div>

        {err && <div style={styles.errBox}>{err}</div>}

        {isLoading ? (
          <div style={styles.noManut}>Caricamento...</div>
        ) : documenti.length === 0 ? (
          <div style={styles.noManut}>Nessun documento per questo apparecchio.</div>
        ) : (
          <div style={styles.docList}>
            {documenti.map((f) => {
              const url = `${UPLOADS_URL}/${f.path ? f.path.replace('/uploads/', '') : f.nome_file}`;
              const isImg = f.mime_type && f.mime_type.startsWith('image/');
              return (
                <div key={f.id} style={styles.docRow}>
                  {isImg ? <Image size={16} color="#9CA3AF" /> : <FileText size={16} color="#9CA3AF" />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <a href={url} target="_blank" rel="noreferrer" style={styles.docNome}>{f.nome_originale}</a>
                    <div style={styles.docMeta}>
                      {etichetta(f.categoria_documento)}
                      {f.dimensione ? ` · ${peso(f.dimensione)}` : ''}
                    </div>
                  </div>
                  {daEliminare === f.id ? (
                    <>
                      <button onClick={() => elimina.mutate(f.id)} disabled={elimina.isPending} style={styles.confermaDelBtn}>Confermi?</button>
                      <button onClick={() => setDaEliminare(null)} style={styles.annullaDelBtn}>No</button>
                    </>
                  ) : (
                    <button onClick={() => setDaEliminare(f.id)} style={styles.delBtn} title="Elimina"><Trash2 size={15} /></button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ ...styles.actions, marginTop: '1rem' }}>
          <button onClick={onClose} style={styles.cancelBtn}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

// ============ TAB CATALOGO ============
function Catalogo() {
  const qc = useQueryClient();
  const [tipoModal, setTipoModal] = useState(false);
  const [manutModal, setManutModal] = useState(null);
  const { data: tipi = [], isLoading } = useQuery({ queryKey: ['prod-tipi'], queryFn: () => prodottiAPI.listaTipi() });

  return (
    <div>
      <div style={styles.toolbar}>
        <div style={{ flex: 1 }} />
        <button onClick={() => setTipoModal(true)} style={styles.addBtn}><Plus size={16} /> Nuovo tipo prodotto</button>
      </div>
      {isLoading ? <div style={styles.empty}>Caricamento...</div> : tipi.length === 0 ? (
        <div style={styles.empty}><Boxes size={36} color="#9CA3AF" /><p>Nessun tipo prodotto. Creane uno (es. Caldaia, Condizionatore).</p></div>
      ) : (
        <div style={styles.grid}>
          {tipi.map((t) => <TipoCard key={t.id} tipo={t} onAddManut={() => setManutModal(t)} />)}
        </div>
      )}
      {tipoModal && <TipoModal onClose={() => setTipoModal(false)} onDone={() => { setTipoModal(false); qc.invalidateQueries({ queryKey: ['prod-tipi'] }); }} />}
      {manutModal && <ManutTipoModal tipo={manutModal} onClose={() => setManutModal(null)} onDone={() => { setManutModal(null); qc.invalidateQueries({ queryKey: ['manut-tipo', manutModal.id] }); }} />}
    </div>
  );
}

function TipoCard({ tipo, onAddManut }) {
  const { data: manut = [] } = useQuery({ queryKey: ['manut-tipo', tipo.id], queryFn: () => prodottiAPI.manutenzioniTipo(tipo.id) });
  return (
    <div style={styles.prodCard}>
      <div style={styles.prodHead}>
        <div style={styles.prodTipo}>{tipo.nome}</div>
        <span style={styles.numBadge}>{manut.length} manut.</span>
      </div>
      {tipo.descrizione && <div style={styles.prodMeta}>{tipo.descrizione}</div>}
      <div style={styles.manutTipoList}>
        {manut.map((m) => (
          <div key={m.id} style={styles.manutTipoRow}>
            <Wrench size={13} color="#9CA3AF" />
            <span style={{ flex: 1 }}>{m.nome}{m.opzionale ? ' (opz.)' : ''}</span>
            <span style={styles.cadenza}>{m.cadenza_mesi} mesi</span>
          </div>
        ))}
        {manut.length === 0 && <div style={styles.noManut}>Nessuna manutenzione definita</div>}
      </div>
      <button onClick={onAddManut} style={styles.addManutBtn}><Plus size={14} /> Aggiungi manutenzione</button>
    </div>
  );
}

function TipoModal({ onClose, onDone }) {
  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [err, setErr] = useState('');
  const mut = useMutation({ mutationFn: () => prodottiAPI.creaTipo({ nome, descrizione: descrizione || null }), onSuccess: onDone, onError: (e) => setErr(e.response?.data?.error || 'Errore') });
  return (
    <div style={styles.overlay2} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Nuovo tipo prodotto</h3>
        <div style={styles.field}><label style={styles.lbl}>Nome *</label><input value={nome} onChange={(e) => setNome(e.target.value)} style={styles.input} placeholder="es. Condizionatore" autoFocus /></div>
        <div style={styles.field}><label style={styles.lbl}>Descrizione</label><textarea value={descrizione} onChange={(e) => setDescrizione(e.target.value)} style={{ ...styles.input, minHeight: '50px', fontFamily: 'inherit' }} /></div>
        {err && <div style={styles.errBox}>{err}</div>}
        <div style={styles.actions}>
          <button onClick={onClose} style={styles.cancelBtn}>Annulla</button>
          <button onClick={() => { setErr(''); if (!nome.trim()) { setErr('Nome obbligatorio'); return; } mut.mutate(); }} disabled={mut.isPending} style={styles.confirmBtn}>Crea</button>
        </div>
      </div>
    </div>
  );
}

function ManutTipoModal({ tipo, onClose, onDone }) {
  const [form, setForm] = useState({ nome: '', cadenza_mesi: 12, opzionale: false, soglia_arancione: 50, soglia_rossa: 20, descrizione: '' });
  const [err, setErr] = useState('');
  const mut = useMutation({ mutationFn: () => prodottiAPI.creaManutenzioneTipo(tipo.id, form), onSuccess: onDone, onError: (e) => setErr(e.response?.data?.error || 'Errore') });
  const h = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  return (
    <div style={styles.overlay2} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Manutenzione per {tipo.nome}</h3>
        <div style={styles.field}><label style={styles.lbl}>Nome *</label><input value={form.nome} onChange={h('nome')} style={styles.input} placeholder="es. Controllo fumi" autoFocus /></div>
        <div style={styles.row}>
          <div style={styles.field}><label style={styles.lbl}>Cadenza (mesi) *</label><input type="number" value={form.cadenza_mesi} onChange={h('cadenza_mesi')} style={styles.input} /></div>
          <div style={styles.field}><label style={styles.lbl}>Opzionale</label>
            <select value={form.opzionale} onChange={(e) => setForm({ ...form, opzionale: e.target.value === 'true' })} style={styles.input}>
              <option value="false">No</option><option value="true">Sì</option>
            </select>
          </div>
        </div>
        <div style={styles.row}>
          <div style={styles.field}><label style={styles.lbl}>Soglia arancione (%)</label><input type="number" value={form.soglia_arancione} onChange={h('soglia_arancione')} style={styles.input} /></div>
          <div style={styles.field}><label style={styles.lbl}>Soglia rossa (%)</label><input type="number" value={form.soglia_rossa} onChange={h('soglia_rossa')} style={styles.input} /></div>
        </div>
        <div style={styles.infoBox}>La barra diventa arancione sotto la prima soglia, rossa sotto la seconda.</div>
        {err && <div style={styles.errBox}>{err}</div>}
        <div style={styles.actions}>
          <button onClick={onClose} style={styles.cancelBtn}>Annulla</button>
          <button onClick={() => { setErr(''); if (!form.nome.trim()) { setErr('Nome obbligatorio'); return; } mut.mutate(); }} disabled={mut.isPending} style={styles.confirmBtn}>Aggiungi</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  tabs: { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' },
  tab: { padding: '0.6rem 1.2rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' },
  tabActive: { backgroundColor: ACCENT, color: 'white', borderColor: ACCENT },
  toolbar: { display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', alignItems: 'center', flexWrap: 'wrap' },
  select: { flex: 1, maxWidth: '400px', padding: '0.6rem 0.85rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.92rem' },
  addBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' },
  empty: { padding: '3rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', backgroundColor: 'white', borderRadius: '12px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' },
  prodCard: { backgroundColor: 'white', borderRadius: '12px', padding: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  prodHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' },
  prodTipo: { fontWeight: 700, fontSize: '1.05rem' },
  prodMarca: { fontSize: '0.88rem', color: '#4B5563' },
  prodMeta: { fontSize: '0.8rem', color: '#9CA3AF', marginTop: '0.2rem' },
  statoDot: { width: '14px', height: '14px', borderRadius: '50%', flexShrink: 0, marginTop: '0.2rem' },
  numBadge: { fontSize: '0.75rem', backgroundColor: '#F3F4F6', color: '#6B7280', padding: '0.2rem 0.5rem', borderRadius: '6px', fontWeight: 600 },
  maintMini: { display: 'flex', flexDirection: 'column', gap: '0.5rem', margin: '0.85rem 0' },
  maintMiniRow: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  maintMiniNome: { fontSize: '0.78rem', color: '#6B7280', width: '110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 },
  miniTrack: { flex: 1, height: '6px', backgroundColor: '#F3F4F6', borderRadius: '3px', overflow: 'hidden' },
  miniFill: { height: '100%', borderRadius: '3px' },
  cardActions: { display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' },
  documentiBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.55rem', backgroundColor: '#F9FAFB', color: '#374151', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' },
  docList: { display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' },
  docRow: { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.7rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },
  docNome: { fontSize: '0.88rem', fontWeight: 600, color: '#374151', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  docMeta: { fontSize: '0.76rem', color: '#9CA3AF', marginTop: '0.1rem' },
  delBtn: { padding: '0.4rem', backgroundColor: 'transparent', color: '#9CA3AF', border: 'none', borderRadius: '6px', cursor: 'pointer', flexShrink: 0 },
  confermaDelBtn: { padding: '0.35rem 0.6rem', backgroundColor: '#FEE2E2', color: '#991B1B', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem', flexShrink: 0 },
  annullaDelBtn: { padding: '0.35rem 0.6rem', backgroundColor: 'white', color: '#6B7280', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', flexShrink: 0 },
  registraBtn: { flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.55rem', backgroundColor: '#F3E8FF', color: ACCENT, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', marginTop: '0.5rem' },
  manutTipoList: { display: 'flex', flexDirection: 'column', gap: '0.4rem', margin: '0.85rem 0' },
  manutTipoRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#374151' },
  cadenza: { fontSize: '0.78rem', color: '#9CA3AF', fontWeight: 600 },
  noManut: { fontSize: '0.82rem', color: '#9CA3AF', fontStyle: 'italic' },
  addManutBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.5rem', backgroundColor: '#F9FAFB', color: '#374151', border: '1px dashed #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' },
  overlay2: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  modal: { backgroundColor: 'white', borderRadius: '14px', padding: '1.5rem', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflow: 'auto' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.85rem', flex: 1, minWidth: '150px' },
  row: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  lbl: { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.6rem 0.8rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  infoBox: { backgroundColor: '#EFF6FF', color: '#1E40AF', padding: '0.7rem', borderRadius: '8px', fontSize: '0.82rem', marginBottom: '0.85rem' },
  errBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '0.85rem' },
  prodInfo: { padding: '0.7rem', backgroundColor: '#F9FAFB', borderRadius: '8px', fontSize: '0.88rem', fontWeight: 600, marginBottom: '0.85rem' },
  actions: { display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' },
  cancelBtn: { padding: '0.6rem 1.1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  confirmBtn: { padding: '0.6rem 1.2rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
};
